import * as React from 'react';
import styled from '@emotion/styled';
import screenfull from 'screenfull';
import { useTranslation } from 'react-i18next';
import { GameOrientationEnum } from '../../types/enum';

type fullscreenElement = Element | undefined;

interface OrientationLockProps {
  fullscreenElement: fullscreenElement;
  orientation: GameOrientationEnum;
  zIndex: number;
}

/**
 * Set the orientation lock. ONLY allowed when the application is in fullscreen mode.
 * When fullscreen is disabled the orientation lock will automatically revert to 'any'
 * or whatever the default of the web application manifest has set.
 *
 * @param {fullscreenElement} fullscreenElement
 * @param {GameOrientationEnum} requestedOrientation
 * @returns {Promise<void>}
 * @public
 */
export const setScreenLock = async (fullscreenElement: fullscreenElement, requestedOrientation: GameOrientationEnum) => {
  // Make sure we're fullscreen, otherwise we won't be allowed to lock orientation.
  if (screenfull.isEnabled && !screenfull.isFullscreen) {
    try {
      await screenfull.request(fullscreenElement);
    } catch (error) {
      // Silently fail.
      console.error(error);
    }
  }

  /**
   * screen.orientation is not supported by Safari
   * https://developer.mozilla.org/en-US/docs/Web/API/Screen/orientation
   */
  if (
    typeof window?.safari === 'undefined' &&
    // @ts-ignore
    (!screenfull.isEnabled || screenfull.isFullscreen) && (screen?.orientation?.lock && typeof screen.orientation.lock === 'function') && ('orientation' in screen || 'mozOrientation' in screen || 'msOrientation' in screen) &&
    [GameOrientationEnum.LANDSCAPE, GameOrientationEnum.PORTRAIT].includes(requestedOrientation)
  ) {
    
    try {
    // @ts-ignore
      await screen.orientation.lock(requestedOrientation);
    } catch (error) {
      // @ts-ignore
      throw new Error(error);
    }
  }
};

/**
 * First undo screen orientation lock. Then remove fullscreen.
 * @returns {Promise<void>}
 */
export const unsetScreenLock = async () => {
  if (screenfull.isEnabled && screenfull.isFullscreen) {
    try {
      await screenfull.exit();
    } catch (error) {
      // @ts-ignore
      throw new Error(error);
    }
  }

  /**
   * screen.orientation is not supported by Safari
   * https://developer.mozilla.org/en-US/docs/Web/API/Screen/orientation
   */
  if (
    typeof window?.safari === 'undefined' &&
    // @ts-ignore
    (!screenfull.isEnabled || screenfull.isFullscreen) && (screen?.orientation?.lock && typeof screen.orientation.lock === 'function') &&
    ('orientation' in screen || 'mozOrientation' in screen || 'msOrientation' in screen)
  ) {
    try {
      await screen.orientation.unlock();
    } catch (error) {
      // @ts-ignore
      throw new Error(error);
    }
  }
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  backgroundColor: theme.palette.secondary.main,
}));

const Title = styled('div')(({ theme }) => ({
  marginTop: '40px',
  color: theme.palette.common.white,
  textTransform: theme.typography.button.textTransform,
  fontSize: theme.typography.button.fontSize,
  fontFamily: theme.typography.button.fontFamily,
}));

const Icon = styled('i')(({ theme }) => ({
  width: '2rem',
  height: '2rem',
  '& svg': {
    fill: theme.palette.common.white,
  },
}));

const IconPhone = styled('div')(({ theme }) => ({
  height: '50px',
  width: '100px',
  border: `3px solid ${theme.palette.common.white}`,
  borderRadius: '10px',
  willChange: 'transform',
  '@media screen and (orientation: landscape)': {
    animation: `$rotate-device-portrait 1.5s ${theme.transitions.easing.easeInOut} infinite alternate`,
  },
  '@media screen and (orientation: portrait)': {
    animation: `$rotate-device-landscape 1.5s ${theme.transitions.easing.easeInOut} infinite alternate`,
  },
  '@keyframes rotate-device-portrait': {
    '0%': { transform: 'rotate(0deg)' },
    '50%': { transform: 'rotate(-90deg)' },
    '100%': { transform: 'rotate(-90deg)' },
  },
  '@keyframes rotate-device-landscape': {
    '0%': { transform: 'rotate(-90deg)' },
    '50%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(0deg)' },
  },
}));

// Todo: Can't perform a React state update on an unmounted component.
//       This is a no-op, but it indicates a memory leak in your application.
//       To fix, cancel all subscriptions and asynchronous tasks in a useEffect cleanup function.
export const OrientationLock = (props: OrientationLockProps) => {
  const {
    fullscreenElement,
    orientation,
    zIndex = 2,
  } = props;
  const { t } = useTranslation('orientation_lock');
  const [state, setState] = React.useState({
    match: false,
    lock: GameOrientationEnum.ANY,
    lockActive: false,
    fullScreenActive: false,
  });

  console.log("fullscreenElement,orientation,", fullscreenElement,orientation)

  /**
   * Set orientation overlay and values, mainly for iOS web users as they don't have
   * screen.orientation support. This method is usually called from an orientation change
   * or resize event. showOrientationLock() and setScreenLock() can be called at the same
   * time, because orientation-lock element won't show anyway if orientation lock API is working.
   *
   * CSS media query fires before the orientationchange. Hence why we call this method on
   * resize instead.
   */
  const showOrientationLock = () => {
    let rotationDirection = GameOrientationEnum.ANY;
    if (window.matchMedia(`(orientation: ${GameOrientationEnum.PORTRAIT})`).matches) {
      rotationDirection = GameOrientationEnum.PORTRAIT;
    }

    if (window.matchMedia(`(orientation: ${GameOrientationEnum.LANDSCAPE})`).matches) {
      rotationDirection = GameOrientationEnum.LANDSCAPE;
    }

    if ([GameOrientationEnum.LANDSCAPE, GameOrientationEnum.PORTRAIT].includes(orientation)) {
      setState((prevState) => ({
        ...prevState,
        match: orientation === rotationDirection,
        lock: orientation,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        match: true,
        lock: GameOrientationEnum.ANY,
      }));
    }

    setState((prevState) => ({
      ...prevState,
      lockActive: !state.match,
    }));
  };

  /**
   * Show fullscreen message, which users can tap to go back into fullscreen and orientation
   * lock if possible.
   */
  const showFullscreenState = () => {
    if (!screenfull.isEnabled) return;

    setState((prevState) => ({
      ...prevState,
      fullScreenActive: !screenfull.isFullscreen,
    }));
  };

  /**
   * Set overlays and events to handle fullscreen and screen-lock orientation.
   * Fullscreen API should be called from a user action, hence this needs to be called first.
   * Orientation lock API needs the fullscreen to be enabled already.
   */
  React.useEffect(() => {
    const handler = async () => {
      await setScreenLock(fullscreenElement, orientation);
      await showOrientationLock();
      // await showFullscreenState();
      document.addEventListener('fullscreenchange', showFullscreenState);
      window.addEventListener('resize', showOrientationLock);
    };

    if (fullscreenElement) {
      try {
        handler();
      } catch (error) {
        console.error(error);
      }
    }

    return () => {
      window.removeEventListener('resize', showOrientationLock);
      document.removeEventListener('fullscreenchange', showFullscreenState);
    };
  }, [fullscreenElement, orientation]);

  return (
    <>
      {state.fullScreenActive && (
        <Root
          onClick={() => setScreenLock(fullscreenElement, orientation)}
          style={{ zIndex: zIndex + 1 }}
        >
          <Icon>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
              <path
                d="M0 180V56c0-13.3 10.7-24 24-24h124c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12H64v84c0 6.6-5.4 12-12 12H12c-6.6 0-12-5.4-12-12zM288 44v40c0 6.6 5.4 12 12 12h84v84c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12V56c0-13.3-10.7-24-24-24H300c-6.6 0-12 5.4-12 12zm148 276h-40c-6.6 0-12 5.4-12 12v84h-84c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h124c13.3 0 24-10.7 24-24V332c0-6.6-5.4-12-12-12zM160 468v-40c0-6.6-5.4-12-12-12H64v-84c0-6.6-5.4-12-12-12H12c-6.6 0-12 5.4-12 12v124c0 13.3 10.7 24 24 24h124c6.6 0 12-5.4 12-12z"/>
            </svg>
          </Icon>
          <Title>{t('tap_for_fullscreen', 'Tap for fullscreen')}</Title>
        </Root>
      )}
      {state.lockActive && !state.match && (
        <Root style={{ zIndex }}>
          <IconPhone/>
          <Title>{t('rotate_your_phone', 'Rotate your device')}</Title>
        </Root>
      )}
    </>
  );
};
